import { AxiosResponse } from 'axios'
import { SET_TICKETS, SET_TICKET, SET_ERRORMSG, SET_TICKET_UNREAD, SET_TICKET_IN_LIST, SET_TICKET_IN_THREAD } from '../mutation_types'
import {
    ASSIGN_TICKET,
    CLEAR_TICKETS_ISLOADING,
    CLEAR_TICKET_UNREAD,
    DELETE_TICKET,
    EDIT_TICKET,
    GET_TICKETS,
    GET_TICKET_UNREAD,
    ADD_TICKET,
    CLOSE_TICKET,
    MARK_TICKET_READ,
    SET_TICKET_STATUS,
    UPSERT_TICKET,
} from '../action_types'
import { StateObject } from '@/dtos/state.dto'
import Ticket from '@/services/ticket'
import { Module } from 'vuex'
import { EditTicket, GetTicket, RegisterTicket, TicketUnread } from '@/dtos/ticket.dto'
import { isOlderCopy } from '@/utils/ticket'

const EMPTY_UNREAD: TicketUnread = { tickets: 0, messages: 0, side: null }

// The tickets a reload brought, except any changed here after it left
// (renamed): that one stays as it is now.
const keepNewer = (listed: any, loaded: any) => {
    if (!Array.isArray(listed) || !Array.isArray(loaded)) return loaded
    const byId = new Map(listed.map((ticket: any) => [ticket?.id, ticket]))
    return loaded.map((ticket: any) => {
        const kept = byId.get(ticket?.id)
        return kept && isOlderCopy(ticket, kept) ? kept : ticket
    })
}

const ticket: Module<any, {}> = {
    state: {
        ticket: {
            data: null,
            isLoading: false
        },
        tickets: {
            data: null,
            isLoading: false
        },
        // What the signed-in side has waiting across its tickets — the badge
        // on Tickets in the navs (utils/ticket_notifications keeps it current).
        ticketUnread: {
            data: EMPTY_UNREAD,
            isLoading: false,
            loadedAt: null
        }
    },
    getters: {
        ticket: (state: { ticket: any }) => state.ticket,
        tickets: (state: { tickets: any }) => state.tickets,
        ticketUnread: (state: { ticketUnread: any }) => state.ticketUnread
    },
    mutations: {
        [SET_TICKET]: (state: { ticket: StateObject }, payload: { data: null; status: any; isLoading: any }) => { payload.data != null ? state.ticket.data = payload.data : null, state.ticket.isLoading = payload.isLoading },
        [SET_TICKETS]: (state: { tickets: StateObject }, payload: { data: any; status: any; isLoading: any }) => {
            if (payload.data != null) state.tickets.data = keepNewer(state.tickets.data, payload.data)
            state.tickets.isLoading = payload.isLoading
        },
        [SET_TICKET_UNREAD]: (state: { ticketUnread: any }, payload: { data?: TicketUnread | null; isLoading: boolean }) => {
            if (payload.data != null) {
                state.ticketUnread.data = payload.data
                state.ticketUnread.loadedAt = Date.now()
            }
            state.ticketUnread.isLoading = payload.isLoading
        },
        // A ticket as the API last described it, merged over the one in the
        // list (its status, newest message, unread count) — or added, when the
        // whole record is given and it is not listed yet.
        [SET_TICKET_IN_LIST]: (state: { tickets: StateObject }, payload: any) => {
            const list = state.tickets.data
            if (!Array.isArray(list) || !payload?.id) return
            const index = list.findIndex((item: any) => item.id === payload.id)
            if (index === -1) {
                if (payload.subject != null) list.unshift(payload)
                return
            }
            list.splice(index, 1, { ...list[index], ...payload })
        }
    },
    actions: {
        [ADD_TICKET]: async({ commit }: any, payload: RegisterTicket) => {
            commit(SET_TICKET, { data: null, isLoading: true })

            let result: AxiosResponse = await Ticket.registerTicket(payload)

            if(result) {
                if (result.data.status) {
                    commit(SET_TICKET, { data: result.data.data, isLoading: false })
                    return true;
                }
                commit(SET_TICKET, { data: null, isLoading: false })
                commit(SET_ERRORMSG, result.data.msg)
                return false;
            } else {
                commit(SET_TICKET, { isLoading: false })
                return false;
            }
        },
        // `silent` reloads the list behind what is already shown (a poll),
        // instead of clearing it first.
        [GET_TICKETS]: async({ commit }: any, payload?: GetTicket & { silent?: boolean }) => {
            const { silent, ...params } = payload || {}
            if (!silent) commit(SET_TICKETS, { data: null, isLoading: true })

            let result: AxiosResponse = await Ticket.getTicket(params)

            if(result) {
                if (result.data.status) {
                    commit(SET_TICKETS, { data: result.data.data, isLoading: false })
                    return true;
                }
                commit(SET_TICKETS, { data: silent ? undefined : null, isLoading: false })
                commit(SET_ERRORMSG, result.data.msg)
                return false;
            } else {
                commit(SET_TICKETS, { isLoading: false })
                return false;
            }
        },
        [EDIT_TICKET]: async({ commit }: any, payload: EditTicket) => {
            commit(SET_TICKET, { data: null, isLoading: true })

            // The API's own errors are toasted by its interceptor; a save that
            // never got an answer is not. A timeout already said so there.
            let result: AxiosResponse
            try {
                result = await Ticket.editTicket(payload)
            } catch (error: any) {
                commit(SET_TICKET, { isLoading: false })
                if (error?.code !== 'ECONNABORTED') {
                    commit(SET_ERRORMSG, "Your changes couldn't be saved. Check your connection and try again.")
                }
                return false;
            }

            if(result) {
                if (result.data.status) {
                    commit(SET_TICKET, { data: result.data.data, isLoading: false })
                    commit(SET_TICKET_IN_LIST, result.data.data)
                    commit(SET_TICKET_IN_THREAD, result.data.data)
                    return true;
                }
                commit(SET_TICKET, { data: null, isLoading: false })
                commit(SET_ERRORMSG, result.data.msg)
                return false;
            } else {
                commit(SET_TICKET, { isLoading: false })
                return false;
            }
        },
        [CLOSE_TICKET]: async({ commit }: any, payload: EditTicket) => {
            commit(SET_TICKET, { data: null, isLoading: true })

            let result: AxiosResponse = await Ticket.closeTicket(payload)

            if(result) {
                if (result.data.status) {
                    commit(SET_TICKET, { data: result.data.data, isLoading: false })
                    commit(SET_TICKET_IN_LIST, result.data.data)
                    return true;
                }
                commit(SET_TICKET, { data: null, isLoading: false })
                commit(SET_ERRORMSG, result.data.msg)
                return false;
            } else {
                commit(SET_TICKET, { isLoading: false })
                return false;
            }
        },
        [DELETE_TICKET]: async({ commit }: any, payload: string) => {
            commit(SET_TICKET, { data: null, isLoading: true })

            let result: AxiosResponse = await Ticket.deleteTicket(payload)

            if(result) {
                if (result.data.status) {
                    commit(SET_TICKET, { data: result.data.data, isLoading: false })
                    return true;
                }
                commit(SET_TICKET, { data: null, isLoading: false })
                commit(SET_ERRORMSG, result.data.msg)
                return false;
            } else {
                commit(SET_TICKET, { isLoading: false })
                return false;
            }
        },
        [GET_TICKET_UNREAD]: async({ commit }: any) => {
            commit(SET_TICKET_UNREAD, { isLoading: true })

            let result: AxiosResponse = await Ticket.getUnread()

            if (result && result.data.status) {
                commit(SET_TICKET_UNREAD, { data: result.data.data, isLoading: false })
                return result.data.data;
            }
            commit(SET_TICKET_UNREAD, { isLoading: false })
            return null;
        },
        // The conversation is open: its unread messages are read. The list
        // and the badge follow at once, without waiting for the next poll.
        [MARK_TICKET_READ]: async({ commit, dispatch }: any, payload: { id: string }) => {
            let result: AxiosResponse = await Ticket.markRead(payload.id)

            if (result && result.data.status) {
                const { side, readAt } = result.data.data || {}
                commit(SET_TICKET_IN_LIST, {
                    id: payload.id,
                    unreadCount: 0,
                    isUnread: false,
                    ...(side === 'staff' ? { staffLastReadAt: readAt } : side === 'organization' ? { organizationLastReadAt: readAt } : {}),
                })
                dispatch(GET_TICKET_UNREAD)
                return true;
            }
            return false;
        },
        [UPSERT_TICKET]: ({ commit }: any, payload: any) => {
            commit(SET_TICKET_IN_LIST, payload)
        },
        // Staff give a ticket to someone (assigneeId), or to nobody (null). The
        // list and the open conversation take the ticket as it now stands.
        [ASSIGN_TICKET]: async({ commit }: any, payload: { id: string; assigneeId: string | null }) => {
            let result: AxiosResponse
            try {
                result = await Ticket.assign(payload.id, payload.assigneeId)
            } catch (error: any) {
                if (error?.code !== 'ECONNABORTED') commit(SET_ERRORMSG, "The ticket couldn't be assigned. Check your connection and try again.")
                return null;
            }
            if (result && result.data.status) {
                commit(SET_TICKET_IN_LIST, result.data.data)
                commit(SET_TICKET_IN_THREAD, result.data.data)
                return result.data.data;
            }
            if (result) commit(SET_ERRORMSG, result.data.msg)
            return null;
        },
        // Moves a ticket along its lifecycle — `to`: in_progress, resolved,
        // closed or reopened — with an optional remark.
        [SET_TICKET_STATUS]: async({ commit }: any, payload: { id: string; to: string; comment?: string }) => {
            let result: AxiosResponse
            try {
                result = await Ticket.setStatus(payload.id, payload.to, payload.comment)
            } catch (error: any) {
                if (error?.code !== 'ECONNABORTED') commit(SET_ERRORMSG, "The ticket couldn't be updated. Check your connection and try again.")
                return null;
            }
            if (result && result.data.status) {
                commit(SET_TICKET_IN_LIST, result.data.data)
                commit(SET_TICKET_IN_THREAD, result.data.data)
                return result.data.data;
            }
            if (result) commit(SET_ERRORMSG, result.data.msg)
            return null;
        },
        [CLEAR_TICKET_UNREAD]: ({ commit }: any) => {
            commit(SET_TICKET_UNREAD, { data: EMPTY_UNREAD, isLoading: false })
        },
        [CLEAR_TICKETS_ISLOADING]: ({ commit }) => {
            commit(SET_TICKET, { isLoading: false })
            commit(SET_TICKETS, { isLoading: false })
        }
    }
}

export default ticket
